import React, { useEffect, useMemo, useRef, useState } from "react";
import "./howitworks_v4.css";

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const pad4 = (n) => String(n).padStart(4, "0");

export default function HowItWorksV5() {
  // 1) TEXT STEPS (what you show on the left/right)
  const steps = useMemo(
    () => [
      {
        subtitle: "Find a loan.",
        body:
          "Pick the loan that fits you. No black boxes, no blind trust. Filter by grade, APY, and more. Open any loan to see the details before you commit.",
      },
      {
        subtitle: "Fund a loan.",
        body:
          "Expand a listing to explore everything from APY to year built. Choose how much to deposit (minimums + increments apply), then click fund.",
      },
      {
        subtitle: "Trade a loan.",
        body:
          "You'll receive tokens representing your share of the loan and its cashflow. Hold for yield, or exit anytime by selling your shares. It’s a lending market.",
      },
    ],
    []
  );

  /**
   * 2) CLIP SEQUENCE (THIS IS THE WHOLE EXPERIENCE)
   * Each clip plays fully, then the next.
   * Reverse happens automatically when scroll reverses.
   *
   * clip: folder name in public/frames/<clip>/
   * frames: number of PNGs in that folder (0001..)
   * stepIndex: which text block to show while this clip is active
   */
  const sequence = useMemo(
    () => [
      { clip: "FindOpenFINAL", frames: 118, stepIndex: 0 },
      { clip: "FindClosedFINAL", frames: 43, stepIndex: 1 },     // transition into step 2 text
      { clip: "FundOpenFINAL", frames: 88, stepIndex: 1 },
      { clip: "FundClosedFINAL", frames: 43, stepIndex: 2 },     // transition into step 3 text
      { clip: "PortfolioOpenFINAL", frames: 88, stepIndex: 2 },
    ],
    []
  );

  // 3) “Apple smooth” feel knobs
  const MAX_DPR = 1.5;              // cap DPR for perf
  const SCROLL_VH_PER_CLIP = 120;   // how much scroll runway per clip (bigger = smoother)
  const SPRING_K = 260;             // stiffness
  const SPRING_C = 34;              // damping (higher = less overshoot)

  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  // Active step text
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  // Cache: key => ImageBitmap | HTMLImageElement
  const cacheRef = useRef(new Map());
  const inFlightRef = useRef(new Set());

  // Precomputed timeline
  const timeline = useMemo(() => {
    const starts = [];
    let total = 0;
    for (const seg of sequence) {
      starts.push(total);
      total += seg.frames;
    }
    return { starts, totalFrames: total };
  }, [sequence]);

  // Scroll target (0..1) and smoothed progress (0..1)
  const targetTRef = useRef(0);
  const tRef = useRef(0);
  const vRef = useRef(0);

  // Helpers
  const ensureCanvasSize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
  };

  const frameUrl = (clip, frame) => {
    // IMPORTANT: these must be in frontend/public/frames/<clip>/<0001>.png
    return `/frames/${clip}/${pad4(frame)}.png`;
  };

  const loadFrame = async (clip, frame) => {
    const key = `${clip}:${frame}`;
    const cache = cacheRef.current;
    if (cache.has(key) || inFlightRef.current.has(key)) return;
    inFlightRef.current.add(key);

    const url = frameUrl(clip, frame);

    try {
      const res = await fetch(url, { cache: "force-cache" });
      if (!res.ok) return;

      const blob = await res.blob();

      try {
        const bmp = await createImageBitmap(blob);
        cache.set(key, bmp);
        return;
      } catch {
        // fallback to <img>
      }

      await new Promise((resolve) => {
        const img = new Image();
        img.decoding = "async";
        img.onload = () => {
          cache.set(key, img);
          resolve();
        };
        img.onerror = () => resolve(); // never crash
        img.src = url;
      });
    } finally {
      inFlightRef.current.delete(key);
    }
  };

  const getAsset = (clip, frame, nearestMax = 12) => {
    const cache = cacheRef.current;
    const exact = `${clip}:${frame}`;
    if (cache.has(exact)) return cache.get(exact);

    for (let d = 1; d <= nearestMax; d++) {
      const k1 = `${clip}:${frame - d}`;
      const k2 = `${clip}:${frame + d}`;
      if (cache.has(k1)) return cache.get(k1);
      if (cache.has(k2)) return cache.get(k2);
    }
    return null;
  };

  const drawContain = (ctx, asset, cw, ch) => {
    const iw = asset.width;
    const ih = asset.height;

    const scale = Math.min(cw / iw, ch / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = (cw - dw) / 2;
    const dy = (ch - dh) / 2;

    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(asset, dx, dy, dw, dh);
  };

  // Map scroll to target timeline position
  useEffect(() => {
    const recomputeTarget = () => {
      const el = wrapRef.current;
      if (!el) return;

      const r = el.getBoundingClientRect();
      const topAbs = r.top + window.scrollY;

      const vh = Math.max(1, window.innerHeight || 1);
      const runwayPx = (sequence.length * SCROLL_VH_PER_CLIP / 100) * vh;

      const y = window.scrollY;
      const t = (y - topAbs) / Math.max(1, runwayPx);
      targetTRef.current = clamp(t, 0, 1);
    };

    const onScroll = () => recomputeTarget();
    const onResize = () => {
      ensureCanvasSize();
      recomputeTarget();
    };

    recomputeTarget();
    ensureCanvasSize();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [sequence.length]);

  // Main RAF: spring → figure out which clip/frame → preload → draw
  useEffect(() => {
    let raf = 0;
    let lastT = 0;

    const tick = (now) => {
      raf = 0;

      if (!lastT) lastT = now;
      const dt = Math.min(0.05, (now - lastT) / 1000);
      lastT = now;

      // spring t toward targetT
      const target = targetTRef.current;
      const x = tRef.current;
      let v = vRef.current;

      const a = SPRING_K * (target - x) - SPRING_C * v;
      v += a * dt;
      let nx = x + v * dt;

      // clamp
      if (nx < 0) { nx = 0; v = 0; }
      if (nx > 1) { nx = 1; v = 0; }

      tRef.current = nx;
      vRef.current = v;

      // Convert [0..1] => global frame index [0..totalFrames-1]
      const totalFrames = timeline.totalFrames;
      const g = clamp(nx * (totalFrames - 1), 0, totalFrames - 1);

      // Find current segment
      // (linear scan is fine for 5 clips; if you add 50, we’ll binary search)
      let segIdx = 0;
      for (let i = 0; i < sequence.length; i++) {
        const start = timeline.starts[i];
        const end = start + sequence[i].frames;
        if (g >= start && g < end) { segIdx = i; break; }
      }

      const seg = sequence[segIdx];
      const segStart = timeline.starts[segIdx];
      const local = g - segStart;

      const frame = clamp(1 + Math.round(local), 1, seg.frames);

      // Update step text
      const stepIndex = seg.stepIndex ?? 0;
      setActiveStepIndex((prev) => (prev === stepIndex ? prev : stepIndex));

      // Preload around current
      const WINDOW = 20;
      const startF = clamp(frame - WINDOW, 1, seg.frames);
      const endF = clamp(frame + WINDOW + 24, 1, seg.frames);
      for (let f = startF; f <= endF; f++) loadFrame(seg.clip, f);

      // Also preload a bit of next clip (for seamless transition)
      const next = sequence[Math.min(sequence.length - 1, segIdx + 1)];
      if (next && next.clip !== seg.clip) {
        for (let f = 1; f <= Math.min(18, next.frames); f++) loadFrame(next.clip, f);
      }

      // Draw
      const canvas = canvasRef.current;
      if (canvas) {
        ensureCanvasSize();
        const ctx = canvas.getContext("2d", { alpha: true, desynchronized: true });
        if (ctx) {
          const asset = getAsset(seg.clip, frame);
          if (asset) {
            drawContain(ctx, asset, canvas.width, canvas.height);
          } else {
            loadFrame(seg.clip, frame);
          }
        }
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [sequence, timeline]);

  // Section height: sticky 100vh + runway
  const heightVh = Math.max(100, 100 + sequence.length * SCROLL_VH_PER_CLIP);

  const activeStep = steps[activeStepIndex] || steps[0];

  return (
    <section
      ref={wrapRef}
      className="hiw4"
      style={{ height: `${heightVh}vh`, background: "rgba(24, 21, 20, 1)" }}
    >
      <div className="hiw4__sticky">
        <div className="hiw4__stage">
          <div className="hiw4__left">
          {/*  <div className="hiw4__kicker">HOW IT WORKS</div> */}
            <div className="hiw4__subtitle">{activeStep.subtitle}</div>
          </div>

          <div className="hiw4__laptopWrap">
            <div className="hiw4__laptopBox">
              <canvas ref={canvasRef} className="hiw4__canvas" />
            </div>
          </div>

          <div className="hiw4__right">
            <p className="hiw4__body">{activeStep.body}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
